<script lang="ts" setup>
import {computed, ref} from "vue";
import {useAuthStore} from "@src/stores/auth";

import AccordionButton from "@src/components/ui/data-display/AccordionButton.vue";
import Collapse from "@src/components/ui/utils/Collapse.vue";
import LabeledTextInput from "@src/components/ui/inputs/LabeledTextInput.vue";
import PasswordInput from "@src/components/ui/inputs/PasswordInput.vue";
import Button from "@src/components/ui/inputs/Button.vue";

const props = defineProps<{
    collapsed: boolean;
    handleToggle: () => void;
}>();

const auth = useAuthStore();

const email = computed(() => auth.user?.email ?? "");

const currentPassword = ref("");
const newPassword = ref("");
const confirmPassword = ref("");

const loading = ref(false);
const error = ref("");
const success = ref("");

const hasSpecialChar = (v: string) => /[^A-Za-z0-9]/.test(v);

const validate = () => {
    error.value = "";
    success.value = "";

    if (!currentPassword.value || !newPassword.value || !confirmPassword.value) {
        error.value = "Preencha todos os campos de senha.";
        return false;
    }

    if (newPassword.value.length < 8) {
        error.value = "A nova senha deve ter pelo menos 8 caracteres.";
        return false;
    }

    if (!hasSpecialChar(newPassword.value)) {
        error.value = "A nova senha deve conter pelo menos 1 caractere especial.";
        return false;
    }

    if (newPassword.value !== confirmPassword.value) {
        error.value = "As senhas não coincidem.";
        return false;
    }

    return true;
};

const handleChangePassword = async () => {
    if (!validate()) return;

    loading.value = true;
    try {
        await auth.changePassword({
            current_password: currentPassword.value,
            new_password: newPassword.value,
            new_password_confirmation: confirmPassword.value,
        });

        success.value = "Senha alterada com sucesso.";
        currentPassword.value = "";
        newPassword.value = "";
        confirmPassword.value = "";
    } catch (e: any) {
        const msg = e?.response?.data?.message;

        if (e?.response?.status === 422) {
            error.value = msg || "Não foi possível alterar a senha. Verifique os campos.";
            return;
        }

        error.value = "Erro ao alterar senha.";
        console.error(e);
    } finally {
        loading.value = false;
    }
};
</script>

<template>
    <AccordionButton
        id="account-settings-toggler"
        :collapsed="props.collapsed"
        aria-controls="account-settings-collapse"
        chevron
        class="w-full flex px-5 py-6 mb-3 rounded focus:outline-none"
        @click="props.handleToggle()"
    >
        <p class="heading-2 text-black/70 dark:text-white/70 mb-1">Conta</p>
        <p class="body-2 text-black/70 dark:text-white/70">Gerencie seu acesso</p>
    </AccordionButton>

    <Collapse id="account-settings-collapse" :collapsed="props.collapsed">
        <div class="px-5 pb-5">
            <!-- Email (readonly) -->
            <LabeledTextInput
                :disabled="true"
                :readonly="true"
                :value="email"
                class="mb-6 opacity-90"
                label="E-mail"
                type="email"
            />

            <p class="heading-3 text-black/70 dark:text-white/70 mb-3">Trocar senha</p>

            <PasswordInput
                :value="currentPassword"
                class="mb-4"
                label="Senha atual"
                @value-changed="(v) => (currentPassword = v)"
            />

            <PasswordInput
                :value="newPassword"
                class="mb-4"
                label="Nova senha"
                @value-changed="(v) => (newPassword = v)"
            />

            <PasswordInput
                :value="confirmPassword"
                class="mb-4"
                label="Confirmar nova senha"
                @value-changed="(v) => (confirmPassword = v)"
            />

            <div v-if="error" class="mb-4 p-3 rounded bg-red-100 border border-red-200 text-red-700 text-sm">
                {{ error }}
            </div>

            <div v-if="success" class="mb-4 p-3 rounded bg-green-100 border border-green-200 text-green-700 text-sm">
                {{ success }}
            </div>

            <Button
                :loading="loading"
                class="contained-primary contained-text w-full py-4"
                @click="handleChangePassword"
            >
                Alterar senha
            </Button>
        </div>
    </Collapse>
</template>